import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type AlertColor = 'Information' | 'Critical' | 'Success' | 'Warning';
export type AlertKind = 'low' | 'high';

export interface QueuedAlert {
  id: string; // stable id (e.g., type+scope+entityId)
  title: string;
  description: string;
  colorTheme: AlertColor;
  kind: AlertKind;
  autoDismissDelay?: number; // ms
}

export interface AlertQueueState {
  queue: QueuedAlert[];
  shownIds: Record<string, boolean>;
}

const initialState: AlertQueueState = {
  queue: [],
  shownIds: {},
};

const alertQueueSlice = createSlice({
  name: 'aag/alertQueue',
  initialState,
  reducers: {
    enqueue: (state, action: PayloadAction<QueuedAlert>) => {
      const alert = action.payload;

      if (state.shownIds[alert.id]) {
        return; // already shown, drop
      }

      // de-dupe against current queue by id
      const exists = state.queue.some((a) => a.id === alert.id);

      if (!exists) {
        state.queue.push(alert);
      }
    },
    dequeue: (state, action: PayloadAction<string>) => {
      const id = action.payload;

      state.queue = state.queue.filter((a) => a.id !== id);
    },
    markShown: (state, action: PayloadAction<string>) => {
      state.shownIds[action.payload] = true;
      state.queue = state.queue.filter((a) => a.id !== action.payload);
    },
    clear: (state) => {
      state.queue = [];
    },
    reset: () => initialState,
  },
});

const { reducer: alertQueueReducer, actions: alertQueueActions } =
  alertQueueSlice;

export { alertQueueActions, alertQueueReducer };
